import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppLang } from 'src/app/shared/services/app-locale.service';
import { ResponseEnvelope } from '../models/response-envelope';

const FE_SUPPORTED: AppLang[] = ['it', 'en'];

/**
 * Self-service profile API for the current user's `siteLang` — the preferred UI language stored on the
 * shared BaseUser. Backed by galactus-core's ProfileController (`/v2/profile/site`):
 *  - `GET  /v2/profile/site`        → ResponseDTO<LanguageCode> (the enum value, or `null` if never set)
 *  - `PATCH /v2/profile/site?lang=` → 204 (a missing/unknown `lang` → 400)
 *
 * The backend `LanguageCode` enum is wider (`en, fr, de, es, it, zh, ja, …`) than the FE's `it`/`en`, so
 * a stored value the FE can't apply — or a `null` — is surfaced as `null` ("no FE-applicable
 * preference"), and writes only ever send `it`/`en`.
 */
@Injectable({ providedIn: 'root' })
export class ProfileService {
  private base = `${environment.apiUrl}/v2/profile/site`;

  constructor(private http: HttpClient) {}

  /**
   * The current user's preferred UI language, or `null` when unset OR not one the FE supports.
   *
   * Unwraps `{ data }` MANUALLY rather than via `unwrapData()` — the latter throws on a `null` envelope,
   * but a `null` siteLang is a VALID "unset" answer here, not a contract error. A transport/HTTP failure
   * is caught to `null` too, so the caller (UserLanguageService's login read) degrades to the local
   * language instead of breaking its subscription.
   */
  getSiteLang(): Observable<AppLang | null> {
    return this.http.get<ResponseEnvelope<string | null>>(this.base, { withCredentials: true }).pipe(
      map((r) => this.toAppLang(r?.data ?? null)),
      catchError(() => of(null)),
    );
  }

  /**
   * Persist the current user's preferred UI language. The value rides as the `lang` query param (the
   * backend binds it to the `LanguageCode` enum); the request body is empty.
   */
  setSiteLang(lang: AppLang): Observable<void> {
    return this.http
      .patch(this.base, null, { params: new HttpParams().set('lang', lang), withCredentials: true })
      .pipe(map(() => void 0));
  }

  private toAppLang(value: string | null): AppLang | null {
    return value !== null && (FE_SUPPORTED as string[]).includes(value) ? (value as AppLang) : null;
  }
}
